import { decrypt, encrypt } from './cryptoMethods';

interface UserData {
    user?: any;
    accessToken?: string;
    refreshToken?: string;
    fireBaseToken?: string;
    loginProvider?: 'google' | 'apple' | 'naver' | 'kakao';
}

// Check if app bridge is available
const isAppBridge = (): boolean => {
    return typeof window !== 'undefined' && !!window.appBridge;
};

// App storage keys
const STORAGE_KEYS = {
    USER: 'user',
    ACCESS_TOKEN: '_access',
    REFRESH_TOKEN: '_refresh',
    FB_TOKEN: '_fb_token',
    EXPIRED_AT: 'expiredAtJoyToken',
    LOGIN_PROVIDERS: 'login_providers',
    MYCHANNEL_ACTIVE_TAB: 'mychannel_active_tab',
    PLATFORM: '_joysamPlatform',
    LANGUAGE: '_language',
    ONBOARDING_COMPLETED: '_onboarding_completed',
    FAILED_TOKEN: '_failed_token',
    APP_RATING_REQUESTED: '_app_rating_requested',
};

// ============================================================================
// Helper to extract string from storage result (handles string or object)
// ============================================================================

/**
 * Extract string value from storage result (handles both string and object responses from native app)
 */
const extractStorageValue = (result: any): string => {
    if (!result) return '';
    if (typeof result === 'string') return result;
    // Handle object responses from native app (e.g., { value: "..." } or { data: "..." })
    if (typeof result === 'object') {
        return result.value || result.data || result.token || '';
    }
    return String(result);
};

/**
 * Helper function to safely decrypt data from storage
 * Handles cases where data might already be in plain text (unencrypted)
 * or encrypted format
 *
 * @param encryptedData - The data from storage (may or may not be encrypted)
 * @param secretKey - Secret key for decryption
 * @returns Decrypted string value
 */
const safeDecrypt = (encryptedData: string, secretKey: string | undefined): string => {
    if (!encryptedData) return '';

    // Check if it looks like a JWT token (xxx.yyy.zzz format)
    // JWT tokens are base64url encoded and have exactly 3 parts separated by dots
    const parts = encryptedData.split('.');
    if (parts.length === 3 && parts.every(part => part.length > 0 && /^[A-Za-z0-9_-]+$/.test(part))) {
        // It's already a plain JWT token, no need to decrypt
        return encryptedData;
    }

    // Try to decrypt the data
    try {
        const decrypted = decrypt(encryptedData, secretKey);

        // If decryption returns empty string, the data was likely not encrypted properly
        // Return the original data as-is
        if (!decrypted) {
            console.warn('Decryption returned empty, using original value');
            return encryptedData;
        }

        // Try to parse JSON if it's a JSON string (wrapped in quotes by JSON.stringify)
        // The encrypt function uses JSON.stringify, so we need to parse it back
        if ((decrypted.startsWith('"') && decrypted.endsWith('"')) ||
            decrypted.startsWith('{') ||
            decrypted.startsWith('[')) {
            try {
                return JSON.parse(decrypted);
            } catch (parseError) {
                // If JSON parse fails, return the decrypted string as-is
                return decrypted;
            }
        }

        return decrypted;
    } catch (decryptError) {
        // If decryption fails, assume the data is already in plain text
        console.warn('Decryption failed, treating as plain text:', decryptError);
        return encryptedData;
    }
};

// ============================================================================
// Login Providers Helpers (used internally)
// ============================================================================

export interface LoginProviders {
    naver?: boolean;
    kakao?: boolean;
    google?: boolean;
    apple?: boolean;
}

/**
 * Helper function to parse providers string to LoginProviders object
 */
const parseProvidersString = (providersString: string): LoginProviders => {
    if (!providersString) return {};
    try {
        return JSON.parse(providersString);
    } catch (error) {
        console.error('Error parsing providers string:', error);
        return {};
    }
};

/**
 * Save login provider to app storage (app bridge only, internal use)
 */
const setAppLoginProvider = async (provider: string): Promise<void> => {
    if (!isAppBridge()) {
        throw new Error('App bridge not available');
    }
    await window.appBridge.storageSetItem(STORAGE_KEYS.LOGIN_PROVIDERS, provider);
};

/**
 * Get login provider from app storage (app bridge only, internal use)
 */
const getAppLoginProvider = async (): Promise<string> => {
    if (!isAppBridge()) {
        throw new Error('App bridge not available');
    }
    return await window.appBridge.storageGetItem(STORAGE_KEYS.LOGIN_PROVIDERS) || '';
};

// ============================================================================
// User Storage Management
// ============================================================================

/**
 * Set user data in app storage (async)
 * If loginProvider is provided, also saves it to login_providers storage
 */
export const setAppStorageUser = async ({ user, accessToken, refreshToken, fireBaseToken, loginProvider }: UserData): Promise<void> => {
    if (!isAppBridge()) {
        throw new Error('App bridge not available');
    }

    try {
        // Set all data in parallel
        const promises = [];

        if (user) {
            const encryptedUser = encrypt(JSON.stringify(user), process.env.SECRET_KEY);
            promises.push(window.appBridge.storageSetItem(STORAGE_KEYS.USER, encryptedUser));
        }

        if (accessToken) {
            const encryptedAccessToken = encrypt(JSON.stringify(accessToken), process.env.SECRET_KEY);
            promises.push(window.appBridge.storageSetItem(STORAGE_KEYS.ACCESS_TOKEN, encryptedAccessToken));
        }

        if (refreshToken !== undefined) {
            const encryptedRefreshToken = encrypt(JSON.stringify(refreshToken), process.env.SECRET_KEY);
            promises.push(window.appBridge.storageSetItem(STORAGE_KEYS.REFRESH_TOKEN, encryptedRefreshToken));
        }

        if (fireBaseToken) {
            const encryptedFbToken = encrypt(JSON.stringify(fireBaseToken), process.env.SECRET_KEY);
            promises.push(window.appBridge.storageSetItem(STORAGE_KEYS.FB_TOKEN, encryptedFbToken));
        }

        if (loginProvider) {
            let providers: LoginProviders = {};
            try {
                const encryptedProvidersString = await window.appBridge.storageGetItem(STORAGE_KEYS.LOGIN_PROVIDERS) || '';
                const decryptedProvidersString = encryptedProvidersString ? safeDecrypt(encryptedProvidersString, process.env.SECRET_KEY) : '';
                providers = parseProvidersString(decryptedProvidersString);
            } catch (error) {
                // If get fails (timeout or error), start with empty object
                console.warn('Failed to get current providers, starting fresh:', error);
                providers = {};
            }

            // Add new provider
            providers[loginProvider] = true;

            const encryptedProviders = encrypt(JSON.stringify(providers), process.env.SECRET_KEY);
            promises.push(window.appBridge.storageSetItem(STORAGE_KEYS.LOGIN_PROVIDERS, encryptedProviders));
        }

        await Promise.all(promises);
    } catch (error) {
        console.error('Error setting app storage:', error);
        throw error;
    }
};

/**
 * Set only user data in app storage
 */
export const setOnlyAppUser = async ({ user }: UserData): Promise<void> => {
    if (!isAppBridge()) {
        throw new Error('App bridge not available');
    }

    try {
        const encryptedUser = encrypt(JSON.stringify(user), process.env.SECRET_KEY);
        await window.appBridge.storageSetItem(STORAGE_KEYS.USER, encryptedUser);
    } catch (error) {
        console.error('Error setting app user:', error);
        throw error;
    }
};

/**
 * Get firebase token from app storage
 */
export const getAppFirebaseToken = async (): Promise<string> => {
    if (!isAppBridge()) {
        throw new Error('App bridge not available');
    }

    try {
        const rawResult = await window.appBridge.storageGetItem(STORAGE_KEYS.FB_TOKEN);
        const encryptedToken = extractStorageValue(rawResult);
        if (!encryptedToken) return '';
        return safeDecrypt(encryptedToken, process.env.SECRET_KEY);
    } catch (error) {
        console.error('Error getting firebase token:', error);
        return '';
    }
};

/**
 * Get access token from app storage
 */
export const getAppAccessToken = async (): Promise<string> => {
    if (!isAppBridge()) {
        throw new Error('App bridge not available');
    }

    try {
        const rawResult = await window.appBridge.storageGetItem(STORAGE_KEYS.ACCESS_TOKEN);
        const encryptedToken = extractStorageValue(rawResult);
        if (!encryptedToken) return '';
        return safeDecrypt(encryptedToken, process.env.SECRET_KEY);
    } catch (error) {
        console.error('Error getting access token:', error);
        return '';
    }
};

/**
 * Get refresh token from app storage
 */
export const getAppRefreshToken = async (): Promise<string> => {
    if (!isAppBridge()) {
        throw new Error('App bridge not available');
    }

    try {
        const rawResult = await window.appBridge.storageGetItem(STORAGE_KEYS.REFRESH_TOKEN);
        const encryptedToken = extractStorageValue(rawResult);
        if (!encryptedToken) return '';
        return safeDecrypt(encryptedToken, process.env.SECRET_KEY);
    } catch (error) {
        console.error('Error getting refresh token:', error);
        return '';
    }
};

/**
 * Get user data from app storage
 */
export const getAppUser = async (): Promise<any> => {
    if (!isAppBridge()) {
        throw new Error('App bridge not available');
    }

    try {
        const rawResult = await window.appBridge.storageGetItem(STORAGE_KEYS.USER);
        const encryptedUser = extractStorageValue(rawResult);
        if (!encryptedUser) return null;
        const decryptedUser = safeDecrypt(encryptedUser, process.env.SECRET_KEY);
        return decryptedUser || null;
    } catch (error) {
        console.error('Error getting app user:', error);
        return null;
    }
};

/**
 * Update access token and user in app storage
 */
export const updateAppStorage = async ({ accessToken, user }: UserData): Promise<void> => {
    if (!isAppBridge()) {
        throw new Error('App bridge not available');
    }

    try {
        const promises = [];

        if (accessToken) {
            const encryptedAccessToken = encrypt(JSON.stringify(accessToken), process.env.SECRET_KEY);
            promises.push(window.appBridge.storageSetItem(STORAGE_KEYS.ACCESS_TOKEN, encryptedAccessToken));
        }

        if (user) {
            const encryptedUser = encrypt(JSON.stringify(user), process.env.SECRET_KEY);
            promises.push(window.appBridge.storageSetItem(STORAGE_KEYS.USER, encryptedUser));
        }

        await Promise.all(promises);
    } catch (error) {
        console.error('Error updating app storage:', error);
        throw error;
    }
};

/**
 * Set expired token time in app storage
 */
export const setAppExpiredToken = async (expired_at: any): Promise<void> => {
    if (!isAppBridge()) {
        throw new Error('App bridge not available');
    }

    try {
        const encryptedExpiry = encrypt(JSON.stringify(expired_at), process.env.SECRET_KEY);
        await window.appBridge.storageSetItem(STORAGE_KEYS.EXPIRED_AT, encryptedExpiry);
    } catch (error) {
        console.error('Error setting expired token:', error);
        throw error;
    }
};

/**
 * Get expired token time from app storage
 */
export const getAppExpiredToken = async (): Promise<string> => {
    if (!isAppBridge()) {
        throw new Error('App bridge not available');
    }

    try {
        const rawResult = await window.appBridge.storageGetItem(STORAGE_KEYS.EXPIRED_AT);
        const encryptedExpiry = extractStorageValue(rawResult);
        if (!encryptedExpiry) return '';
        return safeDecrypt(encryptedExpiry, process.env.SECRET_KEY);
    } catch (error) {
        console.error('Error getting expired token:', error);
        return '';
    }
};

/**
 * Delete all user data from app storage
 */
export const deleteAppStorageUser = async (): Promise<void> => {
    if (!isAppBridge()) {
        throw new Error('App bridge not available');
    }

    try {
        await Promise.all([
            window.appBridge.storageSetItem(STORAGE_KEYS.USER, ''),
            window.appBridge.storageSetItem(STORAGE_KEYS.ACCESS_TOKEN, ''),
            window.appBridge.storageSetItem(STORAGE_KEYS.REFRESH_TOKEN, ''),
            window.appBridge.storageSetItem(STORAGE_KEYS.FB_TOKEN, ''),
        ]);
    } catch (error) {
        console.error('Error deleting app storage user:', error);
        throw error;
    }
};

/**
 * Delete expired token from app storage
 */
export const deleteAppExpiredToken = async (): Promise<void> => {
    if (!isAppBridge()) {
        throw new Error('App bridge not available');
    }

    try {
        await window.appBridge.storageSetItem(STORAGE_KEYS.EXPIRED_AT, '');
    } catch (error) {
        console.error('Error deleting expired token:', error);
        throw error;
    }
};

/**
 * Clear all user storage data
 */
export const clearAppUserStorage = async (): Promise<void> => {
    await deleteAppStorageUser();
    await deleteAppExpiredToken();
};

/**
 * Clear session tokens but keep the social sign-in token (FB_TOKEN).
 *
 * Registration submits FB_TOKEN as input_token. The profile-setup screen calls authenticated APIs
 * that legitimately return 401 before the account exists, which triggers the token-refresh failure
 * path. Wiping FB_TOKEN there makes registration fail with "login token expired" every time.
 * Use deleteAppStorageUser for an explicit user-initiated logout.
 */
export const clearAppSessionStorage = async (): Promise<void> => {
    if (!isAppBridge()) return;

    try {
        await Promise.all([
            window.appBridge.storageSetItem(STORAGE_KEYS.USER, ''),
            window.appBridge.storageSetItem(STORAGE_KEYS.ACCESS_TOKEN, ''),
            window.appBridge.storageSetItem(STORAGE_KEYS.REFRESH_TOKEN, ''),
        ]);
    } catch (error) {
        console.error('Error clearing app session storage:', error);
    }

    await deleteAppExpiredToken();
};

// ============================================================================
// Login Providers Public API
// ============================================================================

/**
 * Save login providers to localStorage (web only)
 * For app bridge, login providers are saved via setAppStorageUser
 */
export const saveLoginProvidersToStorage = async (providers: LoginProviders): Promise<void> => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEYS.LOGIN_PROVIDERS, JSON.stringify(providers));
};

/**
 * Get login providers from storage (works for both web and app bridge)
 */
export const getLoginProviders = async (): Promise<LoginProviders> => {
    try {
        if (isAppBridge()) {
            const rawResult = await window.appBridge.storageGetItem(STORAGE_KEYS.LOGIN_PROVIDERS);
            const encryptedString = extractStorageValue(rawResult);
            if (encryptedString) {
                try {
                    const decrypted = safeDecrypt(encryptedString, process.env.SECRET_KEY);
                    // safeDecrypt already parses JSON, so return directly if it's an object
                    if (typeof decrypted === 'object' && decrypted !== null) {
                        return decrypted as LoginProviders;
                    }
                    // If it's a string, parse it
                    return parseProvidersString(decrypted);
                } catch (decryptError) {
                    return {};
                }
            }
            return {};
        } else if (typeof window !== 'undefined') {
            const providersString = localStorage.getItem(STORAGE_KEYS.LOGIN_PROVIDERS) || '';
            return parseProvidersString(providersString);
        }
        return {};
    } catch (error) {
        console.error('Error getting login providers:', error);
        return {};
    }
};

// ============================================================================
// MyChannel Active Tab Storage
// ============================================================================

/**
 * Save active tab for MyChannel page
 * Works for both web (localStorage) and app (appBridge)
 */
export const saveMyChannelActiveTab = async (tab: string): Promise<void> => {
    try {
        if (isAppBridge()) {
            // Use app bridge storage
            await window.appBridge.storageSetItem(STORAGE_KEYS.MYCHANNEL_ACTIVE_TAB, tab);
        } else if (typeof window !== 'undefined') {
            // Use localStorage for web
            localStorage.setItem(STORAGE_KEYS.MYCHANNEL_ACTIVE_TAB, tab);
        }
    } catch (error) {
        console.error('Error saving active tab:', error);
    }
};

/**
 * Get active tab for MyChannel page
 * Works for both web (localStorage) and app (appBridge)
 */
export const getMyChannelActiveTab = async (): Promise<string | null> => {
    try {
        let tab = null;

        if (isAppBridge()) {
            // Use app bridge storage
            const rawResult = await window.appBridge.storageGetItem(STORAGE_KEYS.MYCHANNEL_ACTIVE_TAB);
            tab = extractStorageValue(rawResult) || null;
        } else if (typeof window !== 'undefined') {
            // Use localStorage for web
            tab = localStorage.getItem(STORAGE_KEYS.MYCHANNEL_ACTIVE_TAB) || null;
        }

        return tab;
    } catch (error) {
        console.error('Error getting active tab:', error);
        return null;
    }
};

/**
 * Clear/Reset active tab for MyChannel page
 * Works for both web (localStorage) and app (appBridge)
 */
export const clearMyChannelActiveTab = async (): Promise<void> => {
    try {
        if (isAppBridge()) {
            // Use app bridge storage
            await window.appBridge.storageRemoveItem(STORAGE_KEYS.MYCHANNEL_ACTIVE_TAB);
        } else if (typeof window !== 'undefined') {
            // Use localStorage for web
            localStorage.removeItem(STORAGE_KEYS.MYCHANNEL_ACTIVE_TAB);
        }
    } catch (error) {
        console.error('Error clearing active tab:', error);
    }
};

// ============================================================================
// Platform Storage
// ============================================================================

/**
 * Save platform to app storage
 * Works for both web (localStorage) and app (appBridge)
 */
export const setAppPlatform = async (platform: string): Promise<void> => {
    try {
        if (isAppBridge()) {
            // Use app bridge storage
            await window.appBridge.storageSetItem(STORAGE_KEYS.PLATFORM, platform);
        } else if (typeof window !== 'undefined') {
            // Use localStorage for web
            localStorage.setItem(STORAGE_KEYS.PLATFORM, platform);
        }
    } catch (error) {
        console.error('Error saving platform:', error);
    }
};

/**
 * Get platform from app storage
 * Works for both web (localStorage) and app (appBridge)
 */
export const getAppPlatform = async (): Promise<string> => {
    try {
        let platform = '';

        if (isAppBridge()) {
            // Use app bridge storage
            const rawResult = await window.appBridge.storageGetItem(STORAGE_KEYS.PLATFORM);
            platform = extractStorageValue(rawResult);
        } else if (typeof window !== 'undefined') {
            // Use localStorage for web
            platform = localStorage.getItem(STORAGE_KEYS.PLATFORM) || '';
        }

        return platform;
    } catch (error) {
        console.error('Error getting platform:', error);
        return '';
    }
};

// ============================================================================
// Language Storage
// ============================================================================

/** 번역이 준비된 언어. 이 목록에 없으면 한국어로 떨어뜨린다. */
const SUPPORTED_LANGUAGES = ['ko', 'en', 'ja'] as const;

/**
 * 브라우저가 알려주는 선호 언어를 ko/en/ja 중 하나로 고른다.
 * 저장된 선택이 없을 때만 쓴다 — 사용자가 한 번 고르면 그 선택이 항상 이긴다.
 * navigator 는 서버(SSR)에 없으므로 없으면 조용히 'ko'.
 */
export const detectBrowserLanguage = (): string => {
    try {
        if (typeof navigator === 'undefined') {
            return 'ko';
        }

        // navigator.languages 는 선호 순서대로 들어오므로 앞에서부터 처음 맞는 것을 쓴다.
        const preferred: string[] = Array.isArray(navigator.languages) && navigator.languages.length > 0
            ? [...navigator.languages]
            : [navigator.language];

        for (const raw of preferred) {
            if (!raw) continue;
            // 'ko-KR', 'en-US', 'ja' 처럼 지역이 붙어 오므로 앞의 언어 코드만 본다.
            const base = raw.toLowerCase().split('-')[0];
            if ((SUPPORTED_LANGUAGES as readonly string[]).includes(base)) {
                return base;
            }
        }

        return 'ko';
    } catch (error) {
        console.error('Error detecting browser language:', error);
        return 'ko';
    }
};

/**
 * Save language preference to app storage
 * Works for both web (localStorage) and app (appBridge)
 */
export const saveLanguage = async (language: string): Promise<void> => {
    try {
        if (isAppBridge()) {
            // Use app bridge storage
            await window.appBridge.storageSetItem(STORAGE_KEYS.LANGUAGE, language);
        } else if (typeof window !== 'undefined') {
            // Use localStorage for web
            localStorage.setItem(STORAGE_KEYS.LANGUAGE, language);
        }
    } catch (error) {
        console.error('Error saving language:', error);
    }
};

/**
 * Get language preference from app storage
 * Works for both web (localStorage) and app (appBridge)
 * Returns the browser's preferred language (ko/en/ja) if not set, 'ko' otherwise
 */
export const getLanguage = async (): Promise<string> => {
    try {
        let language = '';

        if (isAppBridge()) {
            // Use app bridge storage
            const rawResult = await window.appBridge.storageGetItem(STORAGE_KEYS.LANGUAGE);
            language = extractStorageValue(rawResult);
        } else if (typeof window !== 'undefined') {
            // Use localStorage for web
            language = localStorage.getItem(STORAGE_KEYS.LANGUAGE) || '';
        }

        // 저장된 선택이 있으면 그것이 항상 이긴다.
        // 다만 번역이 없는 값(앱이 넣은 'ko-KR' 등)이 들어올 수 있어 언어 코드만 추려 확인한다.
        const saved = language ? language.toLowerCase().split('-')[0] : '';
        if ((SUPPORTED_LANGUAGES as readonly string[]).includes(saved)) {
            return saved;
        }

        // 저장된 선택이 없을 때만 브라우저 언어로 정한다 — 해외 방문자가 가입 전에도 제 언어로 보게.
        return detectBrowserLanguage();
    } catch (error) {
        console.error('Error getting language:', error);
        return 'ko'; // Default to Korean on error
    }
};

// ============================================================================
// Onboarding Status Storage
// ============================================================================

/**
 * Mark onboarding as completed in storage
 * Works for both web (localStorage) and app (appBridge)
 */
export const setOnboardingCompleted = async (): Promise<void> => {
    try {
        if (isAppBridge()) {
            await window.appBridge.storageSetItem(STORAGE_KEYS.ONBOARDING_COMPLETED, 'true');
        } else if (typeof window !== 'undefined') {
            localStorage.setItem(STORAGE_KEYS.ONBOARDING_COMPLETED, 'true');
        }
    } catch (error) {
        console.error('Error setting onboarding completed:', error);
    }
};

/**
 * Check if onboarding has been completed
 * Works for both web (localStorage) and app (appBridge)
 */
export const isOnboardingCompleted = async (): Promise<boolean> => {
    try {
        let status = '';

        if (isAppBridge()) {
            const rawResult = await window.appBridge.storageGetItem(STORAGE_KEYS.ONBOARDING_COMPLETED);
            status = extractStorageValue(rawResult);
        } else if (typeof window !== 'undefined') {
            status = localStorage.getItem(STORAGE_KEYS.ONBOARDING_COMPLETED) || '';
        }

        return status === 'true';
    } catch (error) {
        console.error('Error checking onboarding status:', error);
        return false;
    }
};

// ============================================================================
// App Rating Requested Status Storage
// ============================================================================

/**
 * Mark app rating/review as requested in storage
 * Works for both web (localStorage) and app (appBridge)
 */
export const setAppRatingRequested = async (): Promise<void> => {
    try {
        if (isAppBridge()) {
            await window.appBridge.storageSetItem(STORAGE_KEYS.APP_RATING_REQUESTED, 'true');
        } else if (typeof window !== 'undefined') {
            localStorage.setItem(STORAGE_KEYS.APP_RATING_REQUESTED, 'true');
        }
    } catch (error) {
        console.error('Error setting app rating requested:', error);
    }
};

/**
 * Check if app rating/review has already been requested
 * Works for both web (localStorage) and app (appBridge)
 */
export const isAppRatingRequested = async (): Promise<boolean> => {
    try {
        let status = '';

        if (isAppBridge()) {
            const rawResult = await window.appBridge.storageGetItem(STORAGE_KEYS.APP_RATING_REQUESTED);
            status = extractStorageValue(rawResult);
        } else if (typeof window !== 'undefined') {
            status = localStorage.getItem(STORAGE_KEYS.APP_RATING_REQUESTED) || '';
        }

        return status === 'true';
    } catch (error) {
        console.error('Error checking app rating requested status:', error);
        return false;
    }
};

/**
 * Set the token that recently failed with 401 (to avoid reuse and loops)
 */
export const setAppFailedToken = async (token: string): Promise<void> => {
    try {
        if (isAppBridge()) {
            await window.appBridge.storageSetItem(STORAGE_KEYS.FAILED_TOKEN, token || '');
        } else if (typeof window !== 'undefined') {
            localStorage.setItem(STORAGE_KEYS.FAILED_TOKEN, token || '');
        }
    } catch (error) {
        console.error('Error setting failed token:', error);
    }
};

/**
 * Get the recently failed token from storage
 */
export const getAppFailedToken = async (): Promise<string> => {
    try {
        let token = '';
        if (isAppBridge()) {
            const rawResult = await window.appBridge.storageGetItem(STORAGE_KEYS.FAILED_TOKEN);
            token = extractStorageValue(rawResult);
        } else if (typeof window !== 'undefined') {
            token = localStorage.getItem(STORAGE_KEYS.FAILED_TOKEN) || '';
        }
        return token;
    } catch (error) {
        console.error('Error getting failed token:', error);
        return '';
    }
};

// ============================================================================
// Payment Data Storage (cho redirect qua trang confirm)
// ============================================================================

/**
 * Save payment data to storage
 * Works for both web (localStorage) and app (appBridge)
 */
export const savePaymentData = async (paymentData: any): Promise<void> => {
    try {
        if (isAppBridge()) {
            // Use app bridge storage - encrypt data
            const encrypted = encrypt(JSON.stringify(paymentData), process.env.SECRET_KEY);
            await window.appBridge.storageSetItem('payment_data', encrypted);
        } else if (typeof window !== 'undefined') {
            // Use localStorage for web
            localStorage.setItem('payment_data', JSON.stringify(paymentData));
        }
    } catch (error) {
        console.error('Error saving payment data:', error);
        throw error;
    }
};

/**
 * Get payment data from storage
 * Works for both web (localStorage) and app (appBridge)
 */
export const getPaymentData = async (): Promise<any> => {
    try {
        if (isAppBridge()) {
            // Use app bridge storage - decrypt data
            const rawResult = await window.appBridge.storageGetItem('payment_data');
            const encryptedData = extractStorageValue(rawResult);
            if (encryptedData) {
                return safeDecrypt(encryptedData, process.env.SECRET_KEY) || null;
            }
            return null;
        } else if (typeof window !== 'undefined') {
            // Use localStorage for web
            const dataString = localStorage.getItem('payment_data') || '';
            return dataString ? JSON.parse(dataString) : null;
        }
        return null;
    } catch (error) {
        console.error('Error getting payment data:', error);
        return null;
    }
};

/**
 * Clear payment data from storage
 * Works for both web (localStorage) and app (appBridge)
 */
export const clearPaymentData = async (): Promise<void> => {
    try {
        if (isAppBridge()) {
            await window.appBridge.storageSetItem('payment_data', '');
        } else if (typeof window !== 'undefined') {
            localStorage.removeItem('payment_data');
        }
    } catch (error) {
        console.error('Error clearing payment data:', error);
    }
};

// Export utility function
export { isAppBridge };